/**
 * Inline SVG icons in the reference page's style: 20×20 viewBox, 1.6 stroke, no fill.
 * `<Icon name="trend" />` inherits `currentColor`; size comes from the parent's CSS.
 */
import type { SVGProps } from 'react';
import type { IconName as NavIconName } from '../lib/nav';

export type IconName =
  | NavIconName
  | 'alert' | 'review' | 'check' | 'calendar' | 'clock' | 'sun' | 'menu' | 'close' | 'download'
  | 'plus' | 'inbox' | 'logo' | 'doc' | 'lock' | 'database' | 'mail' | 'info' | 'chevron' | 'external';

const paths: Record<IconName, JSX.Element> = {
  // Reference icons
  grid: (
    <g fill="currentColor" stroke="none">
      <rect x="2" y="2" width="7" height="7" rx="1.5" />
      <rect x="11" y="2" width="7" height="7" rx="1.5" />
      <rect x="2" y="11" width="7" height="7" rx="1.5" />
      <rect x="11" y="11" width="7" height="7" rx="1.5" />
    </g>
  ),
  alert: (<><path d="M10 3l8 14H2z" /><path d="M10 8v4M10 14.5v.5" /></>),
  review: (<><circle cx="10" cy="10" r="7" /><path d="M10 6v4.5M10 13.5v.5" /></>),
  calendar: (<><rect x="3" y="4" width="14" height="13" rx="2" /><path d="M3 8h14M7 2v4M13 2v4" /></>),
  check: <path d="M5 10l3 3 7-7" />,
  clock: (<><circle cx="10" cy="10" r="7" /><path d="M10 6v4l3 2" /></>),
  sun: (<><circle cx="10" cy="10" r="3.5" /><path d="M10 1.5v2M10 16.5v2M1.5 10h2M16.5 10h2M4 4l1.4 1.4M14.6 14.6L16 16M4 16l1.4-1.4M14.6 5.4L16 4" /></>),
  doc: (<><path d="M6 3h8l3 3v11H3V3z" /><path d="M6.5 10l2.2 2.2L13.5 7.5" /></>),

  // SEO HQ, same stroke language
  dashboard: (<><rect x="2.5" y="2.5" width="15" height="15" rx="2.5" /><path d="M2.5 8h15M8 8v9.5" /></>),
  flag: (<><path d="M4.5 17.5V3" /><path d="M4.5 3.5h10l-2 3.5 2 3.5h-10" /></>),
  report: (<><rect x="4" y="2.5" width="12" height="15" rx="2" /><path d="M7 7h6M7 10h6M7 13h3.5" /></>),
  trend: (<><path d="M2.5 14.5l4.5-4.5 3 3 7-7" /><path d="M12.5 6h4.5v4.5" /></>),
  link: (<><path d="M8.5 11.5a3.5 3.5 0 005 0l2.5-2.5a3.5 3.5 0 00-5-5l-1 1" /><path d="M11.5 8.5a3.5 3.5 0 00-5 0L4 11a3.5 3.5 0 005 5l1-1" /></>),
  pulse: <path d="M2 10h3.5l2-5 3 10 2-5H18" />,
  pin: (<><path d="M10 18s5.5-5.2 5.5-9.5a5.5 5.5 0 00-11 0C4.5 12.8 10 18 10 18z" /><circle cx="10" cy="8.5" r="2" /></>),
  search: (<><circle cx="9" cy="9" r="5.5" /><path d="M13 13l4.5 4.5" /></>),
  venn: (<><circle cx="7.5" cy="10" r="5" /><circle cx="12.5" cy="10" r="5" /></>),
  pen: (<><path d="M13 3.5l3.5 3.5L7 16.5H3.5V13z" /><path d="M11 5.5l3.5 3.5" /></>),
  sparkle: (<><path d="M10 2.5l1.6 4.4 4.4 1.6-4.4 1.6L10 14.5l-1.6-4.4L4 8.5l4.4-1.6z" /><path d="M15.5 13.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" /></>),
  chat: <path d="M3 4.5h14v9H9l-4 3.5v-3.5H3z" />,
  quote: (<><path d="M3.5 4.5h13v11h-13z" /><path d="M6.5 8.5h2.5v2.5L7.5 13M11 8.5h2.5v2.5L12 13" /></>),
  globe: (<><circle cx="10" cy="10" r="7.5" /><path d="M2.5 10h15M10 2.5c2.2 2.3 3.2 4.8 3.2 7.5S12.2 15.2 10 17.5C7.8 15.2 6.8 12.7 6.8 10S7.8 4.8 10 2.5z" /></>),
  idcard: (<><rect x="2.5" y="4" width="15" height="12" rx="2" /><circle cx="7.5" cy="9" r="1.8" /><path d="M5 13.2c.6-1.3 1.5-2 2.5-2s1.9.7 2.5 2M12 8h3.5M12 11h3.5" /></>),
  shield: (<><path d="M10 2.5l6 2.5v4.5c0 4-2.6 6.8-6 8-3.4-1.2-6-4-6-8V5z" /><path d="M7.5 10l1.8 1.8 3.4-3.6" /></>),
  bars: <path d="M4 16.5V11M8 16.5V6.5M12 16.5V9M16 16.5V3.5M2.5 16.5h15" />,
  key: (<><circle cx="6.5" cy="13.5" r="3.5" /><path d="M9 11l7.5-7.5M13.5 6.5l2 2M11.5 8.5l1.5 1.5" /></>),
  compass: (<><circle cx="10" cy="10" r="7.5" /><path d="M12.8 7.2l-1.6 4-4 1.6 1.6-4z" /></>),
  queue: (<><rect x="3" y="3" width="14" height="4" rx="1.2" /><rect x="3" y="9" width="14" height="4" rx="1.2" /><path d="M3 16.5h9" /></>),
  tree: (<><circle cx="10" cy="4.5" r="2" /><circle cx="4.5" cy="15" r="2" /><circle cx="10" cy="15" r="2" /><circle cx="15.5" cy="15" r="2" /><path d="M10 6.5V13M10 9.5H4.5V13M10 9.5h5.5V13" /></>),
  voice: (<><path d="M3 8.5v3M6 6v8M9 3.5v13M12 6.5v7M15 8v4M18 9.3v1.4" /></>),
  checklist: (<><path d="M3 5l1.5 1.5L7 4M3 10.5L4.5 12 7 9.5M3 16l1.5 1.5L7 15" /><path d="M10 5.5h7M10 11h7M10 16.5h5" /></>),
  at: (<><circle cx="10" cy="10" r="3" /><path d="M13 7v4a2.2 2.2 0 004.4 0V10a7.4 7.4 0 10-3 6" /></>),
  sliders: <path d="M4 3v4M4 11v6M10 3v8M10 15v2M16 3v2M16 9v8M2 9h4M8 13h4M14 7h4" />,
  menu: <path d="M3 5.5h14M3 10h14M3 14.5h14" />,
  close: <path d="M5 5l10 10M15 5L5 15" />,
  download: (<><path d="M10 3v10M6 9.5l4 4 4-4" /><path d="M3.5 16.5h13" /></>),
  plus: <path d="M10 4v12M4 10h12" />,
  inbox: (<><path d="M2.5 11l2.5-7h10l2.5 7v5.5h-15z" /><path d="M2.5 11h4.5l1 2h4l1-2h4.5" /></>),
  logo: <path d="M5 15.5v-4M10 15.5v-8M15 15.5v-11" />,
  lock: (<><rect x="4" y="9" width="12" height="8.5" rx="2" /><path d="M6.5 9V6.5a3.5 3.5 0 017 0V9" /></>),
  database: (<><ellipse cx="10" cy="5" rx="6" ry="2.5" /><path d="M4 5v10c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5V5M4 10c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5" /></>),
  mail: (<><rect x="2.5" y="4.5" width="15" height="11" rx="2" /><path d="M3 5.5l7 5.5 7-5.5" /></>),
  info: (<><circle cx="10" cy="10" r="7" /><path d="M10 9v4.5M10 6.5v.5" /></>),
  chevron: <path d="M8 5l5 5-5 5" />,
  external: (<><path d="M11 3.5h5.5V9" /><path d="M16.5 3.5L9 11" /><path d="M14 11.5v4.5a1 1 0 01-1 1H4.5a1 1 0 01-1-1V7.5a1 1 0 011-1H9" /></>),
};

export function Icon({ name, strokeWidth = 1.6, ...rest }: { name: IconName; strokeWidth?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={name === 'logo' ? 2.4 : strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {paths[name]}
    </svg>
  );
}
